/**
 * FundElite Design Tokens - Global Color System
 * 
 * ENFORCED BRAND GUARDRAIL:
 * - NO blue-family colors (blue, navy, slate-blue, indigo)
 * - NO purple-family colors (purple, violet, indigo)
 * - ONLY green, gold, neutral gray, and red (for errors)
 * 
 * This is the SINGLE SOURCE OF TRUTH for all colors in the SaaS.
 * Any future color additions must stay within this approved palette.
 */

// ============================================
// APPROVED COLOR FAMILIES ONLY
// ============================================

// Green Family (Primary Brand)
const GREEN = {
  50: '#ecfdf5',
  100: '#d1fae5',
  200: '#a7f3d0',
  300: '#6ee7b7',
  400: '#34d399',
  500: '#10b981', // FundElite Primary
  600: '#059669',
  700: '#047857',
  800: '#065f46',
  900: '#064e3b',
  950: '#022c22',
};

// Gold Family (Secondary/Accent/Premium)
const GOLD = {
  50: '#fffbeb',
  100: '#fef3c7',
  200: '#fde68a',
  300: '#fcd34d',
  400: '#fbbf24',
  500: '#f59e0b', // FundElite Gold
  600: '#d97706',
  700: '#b45309',
  800: '#92400e',
  900: '#78350f',
  950: '#451a03',
};

// Neutral Family (TRUE NEUTRAL - no blue tint)
// These are warm/green-neutral grays, NOT slate-blue
const NEUTRAL = {
  50: '#fafaf9',   // Warm off-white
  100: '#f5f5f4',  // Light neutral
  200: '#e7e5e4',  // Soft gray
  300: '#d6d3d1',  // Medium light
  400: '#a8a29e',  // Medium gray
  500: '#78716c',  // True neutral
  600: '#57534e',  // Dark neutral
  700: '#44403c',  // Darker
  800: '#292524',  // Very dark
  900: '#1c1917',  // Near black
  950: '#0c0a09',  // Deepest
};

// Dark Mode Surfaces (Green-Neutral, NOT navy/slate-blue)
const DARK = {
  bg0: '#0f1512',      // Deepest dark green-charcoal
  bg1: '#161c18',      // Primary dark surface
  bg2: '#1d2420',      // Elevated surface
  bg3: '#252d28',      // Card/panel surface
  border: '#2d3830',   // Subtle border
  borderLight: '#384238', // Light border
};

// Red Family (Error/Destructive ONLY)
const RED = {
  50: '#fef2f2',
  100: '#fee2e2',
  200: '#fecaca',
  300: '#fca5a5',
  400: '#f87171',
  500: '#ef4444',
  600: '#dc2626',
  700: '#b91c1c',
  800: '#991b1b',
  900: '#7f1d1d',
};

// ============================================
// CORE BRAND TOKENS (EXPORTED)
// ============================================

export const brandTokens = {
  primary: GREEN,
  secondary: GOLD,
  neutral: NEUTRAL,
  dark: DARK,
  danger: RED,
  
  // DEPRECATED - Do not use slate, use neutral instead
  // Keeping for backwards compatibility but maps to neutral
  slate: NEUTRAL,
};

// ============================================
// ACCENT PRESETS (All Green-Family)
// ============================================

export const accentPresets = {
  fundelite_emerald: {
    id: 'fundelite_emerald',
    name: 'FundElite Emerald',
    description: 'Default brand color',
    colors: {
      accent: GREEN[500],
      accentHover: GREEN[600],
      accentLight: GREEN[100],
      accentDark: GREEN[800],
    },
  },
  deep_teal: {
    id: 'deep_teal',
    name: 'Deep Teal',
    description: 'Professional teal accent',
    colors: {
      accent: '#0d9488',
      accentHover: '#0f766e',
      accentLight: '#ccfbf1',
      accentDark: '#115e59',
    },
  },
  mint_teal: {
    id: 'mint_teal',
    name: 'Mint Teal',
    description: 'Fresh mint accent',
    colors: {
      accent: '#14b8a6',
      accentHover: '#0d9488',
      accentLight: '#99f6e4',
      accentDark: '#0f766e',
    },
  },
  dark_emerald: {
    id: 'dark_emerald',
    name: 'Dark Emerald',
    description: 'Rich emerald accent',
    colors: {
      accent: GREEN[700],
      accentHover: GREEN[800],
      accentLight: GREEN[200],
      accentDark: GREEN[900],
    },
  },
  soft_green: {
    id: 'soft_green',
    name: 'Soft Green',
    description: 'Gentle green accent',
    colors: {
      accent: '#22c55e',
      accentHover: '#16a34a',
      accentLight: '#dcfce7',
      accentDark: '#15803d',
    },
  },
  neutral_premium: {
    id: 'neutral_premium',
    name: 'Neutral Premium',
    description: 'Neutral professional accent',
    colors: {
      accent: NEUTRAL[500],
      accentHover: NEUTRAL[600],
      accentLight: NEUTRAL[200],
      accentDark: NEUTRAL[700],
    },
  },
};

// ============================================
// SEMANTIC TOKENS
// ============================================

export const semanticTokens = {
  // Success = Green (primary brand)
  success: {
    light: GREEN[100],
    DEFAULT: GREEN[500],
    dark: GREEN[700],
  },
  // Warning = Gold (secondary brand)
  warning: {
    light: GOLD[100],
    DEFAULT: GOLD[500],
    dark: GOLD[700],
  },
  // Danger = Red (only for errors)
  danger: {
    light: RED[100],
    DEFAULT: RED[500],
    dark: RED[700],
  },
  // Info = Green (NOT blue) - informational uses green
  info: {
    light: GREEN[100],
    DEFAULT: GREEN[500],
    dark: GREEN[700],
  },
  // Premium/Recommendation = Gold
  premium: {
    light: GOLD[100],
    DEFAULT: GOLD[500],
    dark: GOLD[700],
  },
};

// ============================================
// CONTEXT ACCENT TOKENS
// ============================================

export const contextAccents = {
  platform: {
    tint: `rgba(16, 185, 129, 0.08)`,
    border: `rgba(16, 185, 129, 0.15)`,
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    icon: 'text-emerald-600 dark:text-emerald-400',
  },
  agency: {
    tint: `rgba(16, 185, 129, 0.06)`,
    border: `rgba(16, 185, 129, 0.12)`,
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    icon: 'text-emerald-600 dark:text-emerald-400',
  },
  client: {
    tint: `rgba(16, 185, 129, 0.05)`,
    border: `rgba(16, 185, 129, 0.10)`,
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    icon: 'text-emerald-600 dark:text-emerald-400',
  },
  // Premium context uses gold
  premium: {
    tint: `rgba(245, 158, 11, 0.08)`,
    border: `rgba(245, 158, 11, 0.15)`,
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    icon: 'text-amber-600 dark:text-amber-400',
  },
};

// ============================================
// SUPPORT MODE TOKENS
// ============================================

export const supportModeTokens = {
  readonly: {
    background: 'rgba(28, 25, 23, 0.92)', // Dark neutral (NOT navy)
    border: `rgba(${parseInt(NEUTRAL[400].slice(1,3),16)}, ${parseInt(NEUTRAL[400].slice(3,5),16)}, ${parseInt(NEUTRAL[400].slice(5,7),16)}, 0.15)`,
    text: NEUTRAL[100],
    accent: GREEN[500],
    badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30',
  },
  interactive: {
    background: 'rgba(28, 25, 23, 0.88)',
    border: `rgba(${parseInt(NEUTRAL[400].slice(1,3),16)}, ${parseInt(NEUTRAL[400].slice(3,5),16)}, ${parseInt(NEUTRAL[400].slice(5,7),16)}, 0.20)`,
    text: NEUTRAL[50],
    accent: GREEN[500],
    badge: 'bg-emerald-500/25 text-emerald-200 border-emerald-400/40',
  },
  warning: {
    background: `rgba(245, 158, 11, 0.15)`,
    border: `rgba(245, 158, 11, 0.30)`,
    text: GOLD[100],
    accent: GOLD[500],
    badge: 'bg-amber-500/20 text-amber-200 border-amber-400/30',
  },
};

// ============================================
// APPEARANCE MODES
// ============================================

export const appearanceModes = {
  system: { id: 'system', name: 'System', description: 'Follow system preference' },
  light: { id: 'light', name: 'Light', description: 'Light mode' },
  dark: { id: 'dark', name: 'Dark', description: 'Dark mode' },
};

// ============================================
// LANGUAGE OPTIONS
// ============================================

export const languageOptions = {
  en: { id: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  es: { id: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
};

// ============================================
// CSS CUSTOM PROPERTIES GENERATOR
// ============================================

export const generateCSSVariables = (accentPreset = 'fundelite_emerald', mode = 'dark') => {
  const accent = accentPresets[accentPreset] || accentPresets.fundelite_emerald;
  const isLight = mode === 'light';
  
  return {
    // Brand
    '--fe-primary': GREEN[500],
    '--fe-primary-hover': GREEN[600],
    '--fe-primary-light': GREEN[100],
    '--fe-primary-dark': GREEN[800],
    
    // Accent (from preset)
    '--fe-accent': accent.colors.accent,
    '--fe-accent-hover': accent.colors.accentHover,
    '--fe-accent-light': accent.colors.accentLight,
    '--fe-accent-dark': accent.colors.accentDark,
    
    // Background (neutral, NOT slate-blue)
    '--fe-bg-primary': isLight ? '#ffffff' : DARK.bg0,
    '--fe-bg-secondary': isLight ? NEUTRAL[50] : DARK.bg1,
    '--fe-bg-tertiary': isLight ? NEUTRAL[100] : DARK.bg2,
    
    // Surface
    '--fe-surface': isLight ? '#ffffff' : DARK.bg1,
    '--fe-surface-hover': isLight ? NEUTRAL[50] : DARK.bg2,
    '--fe-surface-elevated': isLight ? '#ffffff' : DARK.bg3,
    
    // Border
    '--fe-border': isLight ? NEUTRAL[200] : DARK.border,
    '--fe-border-light': isLight ? NEUTRAL[100] : DARK.bg2,
    '--fe-border-strong': isLight ? NEUTRAL[300] : DARK.borderLight,
    
    // Text
    '--fe-text-primary': isLight ? NEUTRAL[900] : NEUTRAL[50],
    '--fe-text-secondary': isLight ? NEUTRAL[600] : NEUTRAL[400],
    '--fe-text-muted': isLight ? NEUTRAL[500] : NEUTRAL[500],
    '--fe-text-inverted': isLight ? NEUTRAL[50] : NEUTRAL[900],
    
    // Semantic
    '--fe-success': semanticTokens.success.DEFAULT,
    '--fe-warning': semanticTokens.warning.DEFAULT,
    '--fe-danger': semanticTokens.danger.DEFAULT,
    '--fe-info': semanticTokens.info.DEFAULT, // Now green, not blue
  };
};

// ============================================
// FORBIDDEN COLORS (DO NOT USE)
// ============================================
// The following color families are BANNED from the FundElite SaaS:
// - blue (all shades)
// - navy
// - slate (Tailwind's default has blue tint - use neutral instead)
// - indigo
// - violet
// - purple
// 
// If you need these colors, you must get explicit approval first.
// Use GREEN for primary/info, GOLD for premium/warning, NEUTRAL for structure.

// ============================================
// STATUS/BADGE COLOR MAPPINGS
// ============================================

export const statusColors = {
  // Document States
  uploaded: { bg: 'bg-amber-100', text: 'text-amber-700', darkBg: 'dark:bg-amber-900/30', darkText: 'dark:text-amber-300' },
  pending_review: { bg: 'bg-amber-100', text: 'text-amber-700', darkBg: 'dark:bg-amber-900/30', darkText: 'dark:text-amber-300' },
  approved: { bg: 'bg-emerald-100', text: 'text-emerald-700', darkBg: 'dark:bg-emerald-900/30', darkText: 'dark:text-emerald-300' },
  verified: { bg: 'bg-emerald-100', text: 'text-emerald-700', darkBg: 'dark:bg-emerald-900/30', darkText: 'dark:text-emerald-300' },
  cropped_draft: { bg: 'bg-amber-100', text: 'text-amber-700', darkBg: 'dark:bg-amber-900/30', darkText: 'dark:text-amber-300' },
  replace_needed: { bg: 'bg-red-100', text: 'text-red-700', darkBg: 'dark:bg-red-900/30', darkText: 'dark:text-red-300' },
  
  // Dispute States
  draft: { bg: 'bg-stone-100', text: 'text-stone-700', darkBg: 'dark:bg-stone-900/30', darkText: 'dark:text-stone-300' },
  processing: { bg: 'bg-amber-100', text: 'text-amber-700', darkBg: 'dark:bg-amber-900/30', darkText: 'dark:text-amber-300' },
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', darkBg: 'dark:bg-amber-900/30', darkText: 'dark:text-amber-300' },
  sent: { bg: 'bg-emerald-100', text: 'text-emerald-700', darkBg: 'dark:bg-emerald-900/30', darkText: 'dark:text-emerald-300' },
  ready_to_send: { bg: 'bg-emerald-100', text: 'text-emerald-700', darkBg: 'dark:bg-emerald-900/30', darkText: 'dark:text-emerald-300' },
  sent_to_bureau: { bg: 'bg-emerald-100', text: 'text-emerald-700', darkBg: 'dark:bg-emerald-900/30', darkText: 'dark:text-emerald-300' },
  sent_to_furnisher: { bg: 'bg-emerald-100', text: 'text-emerald-700', darkBg: 'dark:bg-emerald-900/30', darkText: 'dark:text-emerald-300' },
  resolved: { bg: 'bg-emerald-100', text: 'text-emerald-700', darkBg: 'dark:bg-emerald-900/30', darkText: 'dark:text-emerald-300' },
  failed: { bg: 'bg-red-100', text: 'text-red-700', darkBg: 'dark:bg-red-900/30', darkText: 'dark:text-red-300' },
  
  // Context Types
  technical: { bg: 'bg-emerald-100', text: 'text-emerald-700', darkBg: 'dark:bg-emerald-900/30', darkText: 'dark:text-emerald-300' },
  premium: { bg: 'bg-amber-100', text: 'text-amber-700', darkBg: 'dark:bg-amber-900/30', darkText: 'dark:text-amber-300' },
  source_first: { bg: 'bg-amber-100', text: 'text-amber-700', darkBg: 'dark:bg-amber-900/30', darkText: 'dark:text-amber-300' },
  ai_recommended: { bg: 'bg-amber-100', text: 'text-amber-700', darkBg: 'dark:bg-amber-900/30', darkText: 'dark:text-amber-300' },
};

export default {
  brandTokens,
  accentPresets,
  semanticTokens,
  contextAccents,
  supportModeTokens,
  appearanceModes,
  languageOptions,
  generateCSSVariables,
  statusColors,
  // Expose color constants for direct use
  GREEN,
  GOLD,
  NEUTRAL,
  DARK,
  RED,
};
